import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faChartWaterfall } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Cards as CardsIcon } from '@sorare/core/src/atoms/icons/Cards';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  ANY_SPORT_PLAYERS_SLUG_CARDS,
  ANY_SPORT_PLAYERS_SLUG_CARDS_SALES_HISTORY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';
import { FlexCard } from '@sorare/marketplace/src/components/token/FlexCard';
import { useGetTokenSingleSaleDetails } from '@sorare/marketplace/src/hooks/offers/useGetTokenSingleSaleDetails';
import { useIsPrimaryBuyLive } from '@sorare/marketplace/src/hooks/primaryBuy/useIsPrimaryBuyLive';

import type { MobilePlayerPageFooterItem_anyCard } from './__generated__/index.graphql.ts';

const Root = styled(Horizontal)`
  border-radius: var(--unit);
  backdrop-filter: blur(32px);
  background-color: var(--c-nd-50);
  border: 1px solid var(--c-nd-50);
  padding: var(--unit);
`;

const FullSBHorizontal = styled(SBHorizontal)`
  width: 100%;
`;

const Card = styled.div`
  width: calc(var(--unit) * 5);
`;

const ScarcityIconContainer = styled.div`
  width: calc(var(--unit) * 5);
`;

type Props = {
  rarity: Rarity;
  anyCard: Nullable<MobilePlayerPageFooterItem_anyCard>;
  playerSlug: string;
};

export const MobilePlayerPageFooterItem = ({
  rarity,
  anyCard,
  playerSlug,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const offer = useGetTokenSingleSaleDetails(anyCard);
  const primaryBuy = useIsPrimaryBuyLive(anyCard);
  const monetaryAmount = (primaryBuy || offer)?.price;

  return (
    <Root>
      <FullSBHorizontal>
        <Horizontal gap={1.5}>
          {anyCard ? (
            <Card>
              <FlexCard card={anyCard} width={80} />
            </Card>
          ) : (
            <ScarcityIconContainer>
              <ScarcityIcon rarity={rarity} size={8} />
            </ScarcityIconContainer>
          )}

          {monetaryAmount ? (
            <Vertical gap={0.5}>
              <LabelS color="var(--c-nd-600)">
                <FormattedMessage {...glossary.lowestPrice} />
              </LabelS>
              <LabelM bold>
                <AmountWithConversion monetaryAmount={monetaryAmount} column />
              </LabelM>
            </Vertical>
          ) : (
            <LabelL bold color="var(--c-nd-600)">
              <FormattedMessage
                id="player.mobilePageFooterItem.noCardOnSale"
                defaultMessage="No card for sale"
              />
            </LabelL>
          )}
        </Horizontal>
        <Horizontal>
          <IconButton
            to={generateSportContextPath(
              ANY_SPORT_PLAYERS_SLUG_CARDS_SALES_HISTORY,
              {
                params: { slug: playerSlug },
              }
            )}
            color="quaternary"
            size="small"
            icon={faChartWaterfall}
          />
          <Button
            to={`${generateSportContextPath(ANY_SPORT_PLAYERS_SLUG_CARDS, {
              params: { slug: playerSlug },
            })}?${buildFilterQuery({
              [SEARCH_PARAMS.ON_SALE]: true,
              [SEARCH_PARAMS.IN_SEASON]: true,
              [SEARCH_PARAMS.RARITY]: rarity,
            })}`}
            startIcon={<CardsIcon />}
            size="small"
            color="quaternary"
          >
            <FormattedMessage {...glossary.cards} />
          </Button>
        </Horizontal>
      </FullSBHorizontal>
    </Root>
  );
};

MobilePlayerPageFooterItem.fragments = {
  anyCard: gql`
    fragment MobilePlayerPageFooterItem_anyCard on AnyCardInterface {
      slug
      ...FlexCard_anyCard
      ...useIsPrimaryBuyLive_anyCard
      ...useGetTokenSingleSaleDetails_anyCard
    }
    ${FlexCard.fragments.anyCard}
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${useGetTokenSingleSaleDetails.fragments.anyCard}
  ` as TypedDocumentNode<MobilePlayerPageFooterItem_anyCard>,
};
